import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import Apiurl from "../../Helpers/apiUrl";
import { handleApiError } from "../../Helpers/handleApiError ";
import Swal from "sweetalert2";
import { productDB } from "../../db/productDB";
import { decodeData, encodeData } from "../../Helpers/obfuscate";

const getToken = () => localStorage.getItem("u_token");

const getAuthHeader = () => {
  const token = localStorage.getItem("u_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchMasterProduct = createAsyncThunk(
  "products/fetchMaster",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const productsRes = await axios.get(`${Apiurl}user/product`, { headers: getAuthHeader() });
      const products = productsRes.data?.data || [];
      
      // Trigger stock fetch asynchronously without blocking product catalog loading
      dispatch(fetchMasterStock());

      return products;
    } catch (err) {
      console.error("Fetch Error Trace:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { status } = getState().product;
      if (status === 'loading' || status === 'succeeded') {
        return false;
      }
    }
  }
);

export const fetchMasterStock = createAsyncThunk(
  "products/fetchMasterStock",
  async (_, { rejectWithValue }) => {
    try {
      const stockRes = await axios.get(`${Apiurl}user/inventories`, { headers: getAuthHeader() });
      return stockRes.data?.data || [];
    } catch (err) {
      console.error("Fetch Stock Error Trace:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { status } = getState().product; // Note: they seem to share the same status in productSlice
      if (status === 'loading' || status === 'succeeded') {
        return false;
      }
    }
  }
);

export const fetchAdminProduct = createAsyncThunk(
  "product/fetchAdminProduct",
  async ({ page, rowsPerPage, search, sort_by, sort_order, brand, finish, spare_fg, product_typ_group, collection,preferences, priceSegment, category }, { rejectWithValue }) => {

    const token = getToken();
    if (!token) return rejectWithValue({ message: "No token found, authorization failed." });

    try {
      const response = await axios.get(`${Apiurl}admin/product?search=${encodeURIComponent(search || '')}&page=${page + 1}&limit=${rowsPerPage}&sort_by=${sort_by || ''}&sort_order=${sort_order || ''}&brand=${brand || ''}&finish=${finish || ''}&spare_fg=${spare_fg || ''}&product_typ_group=${product_typ_group || ''}&collection=${collection || ''}&preferences=${preferences}&priceSegment=${priceSegment || ''}&category=${category || ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(error.response?.data || { message: "Failed to fetch products." });
    }
  }
);
export const fetchUserProduct = createAsyncThunk(
  "product/fetchUserProduct",
  async ({ page, rowsPerPage, search, brand, finish, spare_fg, product_typ_group, collection, priceSegment, category }, { rejectWithValue }) => {

    const token = getToken();
    if (!token) return rejectWithValue({ message: "No token found, authorization failed." });

    try {
      const response = await axios.get(`${Apiurl}admin/product?search=${encodeURIComponent(search || '')}&page=${page + 1}&limit=${rowsPerPage}&brand=${brand || ''}&finish=${finish || ''}&spare_fg=${spare_fg || ''}&product_typ_group=${product_typ_group || ''}&collection=${collection || ''}&priceSegment=${priceSegment || ''}&category=${category || ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(error.response?.data || { message: "Failed to fetch products." });
    }
  }
);

export const fetchAdminInventory = createAsyncThunk(
  "product/fetchAdminInventory",
  async ({ page, limit, search, sort_by, sort_order }, { rejectWithValue }) => {

    const token = getToken();
    if (!token) return rejectWithValue({ message: "No token found, authorization failed." });

    try {
      const response = await axios.get(`${Apiurl}admin/inventory?search=${search || ''}&page=${page}&limit=${limit}&sort_by=${sort_by}&sort_order=${sort_order}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(error.response?.data || { message: "Failed to fetch products." });
    }
  }
);
export const fetchUserInventory = createAsyncThunk(
  "product/fetchUserInventory",
  async ({ page, limit, search }, { rejectWithValue }) => {

    const token = getToken();
    if (!token) return rejectWithValue({ message: "No token found, authorization failed." });

    try {
      const response = await axios.get(`${Apiurl}admin/inventory?search=${search || ''}&page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(error.response?.data || { message: "Failed to fetch products." });
    }
  }
);

export const fetchProductSuggestions = createAsyncThunk(
  "fetchProductSuggestions",
  async ({ page, limit, search }, { rejectWithValue }) => {
    const token = getToken();
    if (!token) return rejectWithValue({ message: "No token found, authorization failed." });

    try {
      const response = await axios.get(`${Apiurl}admin/sku-suggestion?search=${search || ''}&page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      handleApiError(error, "Please try again later.");
      return rejectWithValue(error.response?.data || { message: "Failed to fetch." });
    }
  }
);


const initialState = {
  masterProduct: [],
  productSuggestions: [],
  adminProducts: [],
  inventory: [],
  count: 0,
  getStatus: "idle",
  error: null,
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
       .addCase(fetchMasterProduct.pending, (state) => {
        state.getStatus = "pending";
        state.error = null;
      })
      .addCase(fetchMasterProduct.fulfilled, (state, action) => {
        state.getStatus = "succeeded";
        state.masterProduct = action.payload.map(item => {
          const mrp = parseFloat(item.mrp) || 0;
          const gst = parseFloat(item.gst) || 0;
          const basePrice = (mrp * 100) / (100 + gst);

          return {
            ...item,
            basePrice: basePrice.toFixed(2),
          };
        });
      })
      .addCase(fetchMasterStock.fulfilled, (state, action) => {
        const stocks = action.payload;
        const stockMap = new Map(
          stocks?.map(item => [String(item.sku_code), {
            available: item.stockAvailable,
            indicator: item.stockIndicator,
            pruning_date: item.pruning_date,
            status: item.status

          }])
        );
        state.masterProduct = state.masterProduct.map(product => {
          const stock = stockMap.get(String(product.sku_code));
          if (stock) {
            return {
              ...product,
              stockAvailable: stock.available ?? 0,
              stockIndicator: stock.indicator ?? null,
               pruning_date: stock.pruning_date ?? '',
              status: stock.status ?? 'Active'
            };
          }
          return product;
        });
      })
      .addCase(fetchMasterProduct.rejected, (state, action) => {
        state.getStatus = "failed";
        state.error = action.payload;
      })
      .addCase(fetchAdminProduct.pending, (state) => {
        state.error = null;
        state.getStatus = "pending";
      })
      .addCase(fetchAdminProduct.fulfilled, (state, action) => {
        state.adminProducts = action.payload.data;
        state.count = action.payload.total;
        state.getStatus = "succeeded";
      })
      .addCase(fetchAdminProduct.rejected, (state, action) => {
        state.error = action.payload;
        state.getStatus = "failed";
      })
      .addCase(fetchUserProduct.pending, (state) => {
        state.error = null;
        state.getStatus = "pending";
      })
      .addCase(fetchUserProduct.fulfilled, (state, action) => {
        state.adminProducts = action.payload.data;
        state.count = action.payload.total;
        state.getStatus = "succeeded";
      })
      .addCase(fetchUserProduct.rejected, (state, action) => {
        state.error = action.payload;
        state.getStatus = "failed";
      })
      .addCase(fetchAdminInventory.pending, (state) => {
        state.error = null;
        state.getStatus = "pending";
      })
      .addCase(fetchAdminInventory.fulfilled, (state, action) => {
        state.inventory = action.payload.data;
        state.count = action.payload.total;
        state.getStatus = "succeeded";
      })
      .addCase(fetchAdminInventory.rejected, (state, action) => {
        state.error = action.payload;
        state.getStatus = "failed";
      })
      .addCase(fetchUserInventory.pending, (state) => {
        state.error = null;
        state.getStatus = "pending";
      })
      .addCase(fetchUserInventory.fulfilled, (state, action) => {
        state.inventory = action.payload.data;
        state.count = action.payload.total;
        state.getStatus = "succeeded";
      })
      .addCase(fetchUserInventory.rejected, (state, action) => {
        state.error = action.payload;
        state.getStatus = "failed";
      })
      .addCase(fetchProductSuggestions.pending, (state) => {
        state.error = null;
        state.getStatus = "pending";
      })
      .addCase(fetchProductSuggestions.fulfilled, (state, action) => {
        state.productSuggestions = action.payload.data;
        state.count = action.payload.pagination.total;
        state.getStatus = "succeeded";
      })
      .addCase(fetchProductSuggestions.rejected, (state, action) => {
        state.error = action.payload;
        state.getStatus = "failed";
      });
  },
});

export default productSlice.reducer;